import * as CustomerActions from "@app/customer/store/actions";
import { LoadingState } from "@app/root-store/api-result.state";
import { User } from "@app/user/model/user";
import { createReducer, on } from "@ngrx/store";
import { UserActions, UserRoleActions } from "../actions";
import { UserManagementState, initialUserManagementState } from "../user-management.state";

const deleteUser = (state: UserManagementState, userId: number) => {
    const users = state.users.result;
    delete users[userId];
    const index = state.activeEntityStaff?.result?.findIndex(user => user.id === userId);

    let entityStaff = state.activeEntityStaff.result;
    if (index >= 0) {
        entityStaff = [...state.activeEntityStaff.result];
        entityStaff.splice(index, 1);
    }

    return {
        ...state,
        users: {
            result: users,
            callState: LoadingState.loaded,
        },
        activeEntityStaff: {
            ...state.activeEntityStaff,
            result: entityStaff,
            callState: LoadingState.loaded,
        },
    };
};

const deleteCustomer = (state: UserManagementState, userId: number) => {
    const users = state.customers.result;
    delete users[userId];
    return {
        ...state,
        customers: {
            result: users,
            callState: LoadingState.loaded,
        },
    };
};

const upsertUser = (state: UserManagementState, user: User) => {
    const index = state.activeEntityStaff?.result?.findIndex(_user => _user?.id === user.id);

    let entityStaff = state.activeEntityStaff.result;
    if (user.entityId === state.activeEntityStaff.entityId || index >= 0) {
        entityStaff = [...state.activeEntityStaff.result, user];
        if (index !== -1) {
            entityStaff.splice(index, 1);
        }
    }

    return {
        ...state,
        users: {
            result: {
                ...state.users.result,
                [user.id]: user,
            },
            callState: LoadingState.loaded,
        },
        activeEntityStaff: {
            ...state.activeEntityStaff,
            result: entityStaff,
        },
    };
};

export const reducer = createReducer(
    initialUserManagementState,
    on(CustomerActions.deleteSuccess, (state, { customer }): UserManagementState => deleteCustomer(state, customer.id)),
    on(
        CustomerActions.createSuccess,
        CustomerActions.updateSuccess,
        (state, { customer }): UserManagementState => upsertUser(state, customer)
    ),
    on(
        UserActions.loadAllEntityUsers,
        (state): UserManagementState => ({
            ...state,
            users: {
                ...state.users,
                callState: LoadingState.loading,
            },
        })
    ),
    on(
        UserActions.loadAllEntityCustomers,
        (state): UserManagementState => ({
            ...state,
            customers: {
                ...state.customers,
                callState: LoadingState.loading,
            },
        })
    ),
    on(
        UserActions.loadEntityUsers,
        (state, { entityId }): UserManagementState => ({
            ...state,
            activeEntityStaff: {
                result: [],
                entityId,
                callState: LoadingState.loading,
            },
        })
    ),
    on(
        UserActions.clearActiveEntityStaff,
        (state): UserManagementState => ({
            ...state,
            activeEntityStaff: {
                result: null,
                entityId: null,
                callState: LoadingState.init,
            },
        })
    ),
    on(
        UserActions.loadActiveProjectUser,
        (state): UserManagementState => ({
            ...state,
            activeProjectUser: {
                result: null,
                callState: LoadingState.loading,
            },
        })
    ),
    on(
        UserActions.updateSuccess,
        UserActions.createSuccess,
        UserActions.upsertSuccessSilent,
        UserActions.getSuccess,
        (state, { user }): UserManagementState => upsertUser(state, user)
    ),
    on(UserActions.deleteSuccess, (state, { user }): UserManagementState => deleteUser(state, user.id)),
    on(
        UserActions.loadAllEntityUsersSuccess,
        (state, { users }): UserManagementState => ({
            ...state,
            users: {
                result: users,
                callState: LoadingState.loaded,
            },
        })
    ),
    on(
        UserActions.loadAllEntityCustomersSuccess,
        (state, { users }): UserManagementState => ({
            ...state,
            customers: {
                result: users,
                callState: LoadingState.loaded,
            },
        })
    ),
    on(
        UserActions.loadEntityUsersSuccess,
        (state, { users }): UserManagementState => ({
            ...state,
            activeEntityStaff: {
                ...state.activeEntityStaff,
                result: users,
                callState: LoadingState.loaded,
            },
        })
    ),
    on(
        UserActions.loadActiveProjectUserSuccess,
        (state, { user }): UserManagementState => ({
            ...state,
            activeProjectUser: {
                result: user,
                callState: LoadingState.loaded,
            },
        })
    ),
    on(UserActions.clearActiveProjectUser, state => ({
        ...state,
        activeProjectUser: {
            result: null,
            callState: LoadingState.init,
        },
    })),
    on(
        UserRoleActions.setCustomerRoleId,
        (state, { customerRole }): UserManagementState => ({
            ...state,
            customerRoleId: customerRole.id,
        })
    ),
    on(
        UserRoleActions.load,
        (state): UserManagementState => ({
            ...state,
            userRoles: {
                ...state.userRoles,
                callState: LoadingState.loading,
            },
        })
    ),
    on(
        UserRoleActions.loadSuccess,
        (state, { userRoles }): UserManagementState => ({
            ...state,
            userRoles: {
                ...state.userRoles,
                result: userRoles,
                callState: LoadingState.loaded,
            },
        })
    )
);
